import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';
import {PersonsService} from 'src/app/services/persons.service';
import {PriceTickerUploadService} from 'src/app/services/priceTickerUpload.service';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../Utils/batch-loader.util";

/**
 * PriceTickerUploadComponent
 *
 * This component is responsible for managing the upload and processing of price ticker data files.
 * It provides a user interface for administrators to upload Excel files containing market price data,
 * view the status of uploaded files, and track processing results.
 *
 * Key Responsibilities:
 * - Handle file selection and upload to the server.
 * - Validate file type (Excel) and size constraints.
 * - Display a list of previously uploaded files with their processing status.
 * - Implement infinite scrolling for efficient display of upload history.
 * - Provide feedback on upload success or failure, including detailed error comments.
 * - Manage UI state for loading indicators, file upload forms, and list display.
 *
 * Features:
 * - **File Upload**: Drag-and-drop or file selection for .xlsx files.
 * - **Validation**: Checks for correct file format and size limits (20MB).
 * - **Upload History**: Lists uploaded files with details like total rows, recorded rows, and defects.
 * - **Infinite Scroll**: Efficiently loads upload history in batches.
 * - **Status Tracking**: Shows success/failure status and comments for each upload.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches upload history from `PriceTickerUploadService`.
 * 3. Fetches uploader details from `PersonsService` to display names.
 * 4. Initializes `BatchLoader` with the upload history.
 * 5. User selects a file -> `handleFileInput` validates and uploads.
 * 6. Service processes file and returns status -> List is refreshed.
 *
 * @component
 * @selector app-price-ticker-upload
 * @example
 * <app-price-ticker-upload></app-price-ticker-upload>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-price-ticker-upload',
  templateUrl: './price-ticker-upload.component.html',
  styleUrls: ['./price-ticker-upload.component.scss']
})
export class PriceTickerUploadComponent implements OnInit, OnDestroy {
  /**
   * Flag indicating if the page is currently loading data.
   * @type {boolean}
   * @default true
   */
  public pgLoad = true;

  /**
   * Generic items array (usage unclear from context, possibly placeholder).
   * @type {any}
   */
  public items: any = [1, 2, 3];

  /**
   * Array holding the list of uploaded files currently displayed.
   * This list is populated by the BatchLoader (subset of fileUploadList).
   * @type {any}
   * @default []
   */
  public filesUploaded: any = [];

  /**
   * Array holding the complete list of uploaded files fetched from the backend.
   * @type {any}
   * @default []
   */
  public fileUploadList: any = [];

  /**
   * Array mapping person IDs to names for displaying uploader information.
   * @type {any}
   * @default []
   */
  public personArray: any = [];

  /**
   * Array of unique person IDs who have uploaded files.
   * @type {any[]}
   * @default []
   */
  public uploadedByPersonIds = [];

  /**
   * Name of the file currently being uploaded.
   * @type {string}
   */
  public fileName: string;

  /**
   * Size of the file currently being uploaded.
   * @type {number}
   * @default 0
   */
  public fileSize = 0;

  /**
   * Comments or status message returned after file upload.
   * @type {string}
   * @default ''
   */
  public fileComments = '';

  /**
   * Flag indicating if a file upload has been initiated.
   * @type {boolean}
   * @default false
   */
  public fileUploadFlag: boolean = false;

  /**
   * Flag indicating the success status of the file upload.
   * @type {boolean}
   * @default false
   */
  public fileuploadStatusFlag: boolean = false;

  /**
   * Flag to control the loader during file upload processing.
   * @type {boolean}
   * @default false
   */
  public uploadFileLoader = false;

  // pagination sorting
  /**
   * Current page number (not actively used with infinite scroll).
   * @type {number}
   * @default 1
   */
  public currentpagenum: number = 1;

  /**
   * Number of items per page (not actively used with infinite scroll).
   * @type {number}
   * @default 15
   */
  public noofitemsPerPage: number = 15;

  /**
   * Flag to control the visibility of the upload success/failure modal.
   * @type {boolean}
   * @default false
   */
  public closeFlag: boolean = false;

  /**
   * Reactive form group for the file upload input.
   * @type {FormGroup}
   */
  public fileUploadForm: FormGroup;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active (not implemented in this component).
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Timeout reference for search input debouncing (unused).
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Constructor for PriceTickerUploadComponent
   *
   * Initializes the component and injects required services.
   *
   * @constructor
   * @param {PriceTickerUploadService} priceTickerUploadService - Service for price ticker upload API operations.
   * @service PriceTickerUploadService
   * @param {PersonsService} personsService - Service for retrieving person details.
   * @service PersonsService
   * @param {DomSanitizer} sant - Service for sanitizing DOM values (unused).
   * @service DomSanitizer
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(public priceTickerUploadService: PriceTickerUploadService, public personsService: PersonsService, public sant: DomSanitizer, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Scrolls to top of page.
   * 2. Resets component state.
   * 3. Tracks current route.
   * 4. Initializes file upload form.
   * 5. Fetches uploaded files history.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.isFiltering = false;
    this.filesUploaded = [];
    this.fileUploadList = [];
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.fileUploadForm = new FormGroup({fileUpload: new FormControl(null)});
    await this.getUploadedFiles();
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.priceTickerUploadService.cancelOnGoingHttpRequests();
  }

  /**
   * Fetches the list of uploaded files and their details.
   *
   * - Calls `getUploadedFiles` service method.
   * - Processes the response to format file details and status.
   * - Fetches uploader names using `PersonsService`.
   * - Initializes the batch loader for infinite scrolling.
   *
   * @async
   * @method getUploadedFiles
   * @returns {Promise<void>}
   */
  public async getUploadedFiles() {
    try {
      let ele = await this.priceTickerUploadService.getUploadedFiles();
      this.filesUploaded = [];
      this.personArray = [];
      this.uploadedByPersonIds = [];
      if (ele.length > 0) {
        ele.forEach(element => {
          if (element.updated_by) {
            this.uploadedByPersonIds.push(element.updated_by);
            let item = {
              date: element.createdAt,
              uploadedBy: '',
              filename: element.file_name.slice(0, element.file_name.length - 19),
              total_rows: element.no_of_rows,
              recorded: element.accepted_rows,
              defects: element.excepted_rows,
              comments: element.comment,
              personId: element.updated_by,
              comment1: '',
              comment2: '',
              success: true
            };
            this.filesUploaded.push(item);
          }
        });
        this.fileUploadList = this.filesUploaded
        this.uploadedByPersonIds = this.uploadedByPersonIds.filter((c, index) => {
          return this.uploadedByPersonIds.indexOf(c) === index;
        });
        await this.uploadedByPersonIds.forEach(async (item, index) => {
          let res: any = await this.personsService.getPersonDetailsById(item);
          let ele = {
            personId: item,
            personName: res.firstName + ` ` + res.lastName
          };
          this.personArray.push(ele);
          if (index == this.uploadedByPersonIds.length - 1) {
            this.filesUploaded.forEach(item => {
              let record = this.personArray.find(element => element.personId == item.personId);
              if (record) {
                item.uploadedBy = record.personName ? record.personName : '';
              }
              if (item.total_rows == item.recorded) {
                item.success = true;
                item.comments = 'File uploaded successfully.';
              }
              if (item.total_rows == item.defects) {
                item.success = false;
                item.comments = 'File uploaded successfully.';
                item.comment1 = 'Encountered problem with all rows.';
                item.comment2 = 'Probable reasons are either from/to count and price consists of zero or null or negative values or characters.';
              }
              if (item.comments.includes('File uploaded successfully. Encountered problem with rows')) {
                item.comment1 = item.comments.split('. ')[1];
                item.comments = item.comments.split('. ')[0];
              }
            });
            this.pgLoad = false;
          }
        });
        this.initializeBatchLoader(); // Reset instance
        this.batchLoader?.reset();     // Clear state
        await this.batchLoader?.loadNextBatch(true); // Load first batch
        this.enableDisablePageLoader = false;
      } else {
        this.pgLoad = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filesUploaded : this.fileUploadList,
      (updatedList) => {
        this.filesUploaded = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Handles file selection and upload process.
   *
   * - Validates file type (Excel .xlsx) and size (< 20MB).
   * - Uploads file to server via `uploadFileToServer`.
   * - Sends file metadata to backend via `sendFileToUpload`.
   * - Refreshes the uploaded files list upon success.
   *
   * @async
   * @method handleFileInput
   * @param {FileList} file - The list of files selected by the user.
   * @returns {Promise<void>}
   */
  public async handleFileInput(file: FileList) {
    try {
      // validate the File Type when drag and drop id done or file changed
      if (file[0].type == 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        // validate the File size less than or equal to 20 MB (Caluculate in Bytes)
        if (file[0].size <= 20971520) {
          const upload = this.priceTickerUploadService.uploadFileToServer(file[0], 'Ambrotechs', 'MarketPriceList', `${file[0].name}`);
          upload.subscribe(async (response) => {
            if (response) {
              this.fileUploadFlag = true;
              this.uploadFileLoader = false;
              const personId = localStorage.getItem('personID');
              let res = await this.priceTickerUploadService.sendFileToUpload(file[0].name, personId, personId, Object.values(response)[0]);
              this.fileComments = res.marketPrice;
              if (res.marketPrice.includes('File uploaded successfully')) {
                this.fileuploadStatusFlag = true;
              } else {
                this.fileuploadStatusFlag = false;
              }
              this.getUploadedFiles();
              this.uploadFileLoader = true;
              this.closeFlag = true;
              this.fileUploadForm.reset();
            } else {
              alert('File upload failed');
            }
          })
        } else {
          alert('Please Upload a .xslx file less than 20mb only');
        }
      } else {
        alert('Please Upload only .xlsx files.');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Closes the upload status modal/message.
   *
   * @method closeUploaded
   * @returns {void}
   */
  closeUploaded() {
    this.closeFlag = false;
  }
}
